import { useEffect, useState } from "react";
import { Play, Pause, Plus, Loader2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { usePlayer } from "../contexts/PlayerContext";
import LicenseModal from "./LicenseModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FeaturedBeats() {
  const [beats, setBeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [licenseBeat, setLicenseBeat] = useState(null);
  const { currentTrack, isPlaying, playTrack } = usePlayer();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/beats?featured=true&limit=10`);
        if (!cancelled) setBeats(res.data.beats || []);
      } catch (err) {
        console.error("Failed to load featured beats:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section
      id="beats"
      data-testid="featured-beats"
      className="py-24 px-6 max-w-7xl mx-auto"
    >
      <div className="mb-12 flex items-end justify-between gap-6 flex-wrap">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase text-gold mb-3">
            Curated Selection
          </p>
          <h2
            className="text-4xl sm:text-5xl tracking-tight font-medium text-[#EAEAEA] mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Featured Beats
          </h2>
          <p className="text-[#737373] text-base max-w-xl">
            Ten pieces hand-picked from the catalog. Different vibes, different keys,
            same craft.
          </p>
        </div>
        <Link
          to="/catalog"
          data-testid="featured-view-catalog"
          className="chrome-dark inline-flex items-center gap-2 px-6 py-3 border border-gold/50 text-[#F2D072] text-xs tracking-[0.25em] uppercase rounded-full transition-all hover:border-gold hover:brightness-110"
        >
          View Full Catalog
          <ArrowRight size={14} />
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-gold" size={32} />
        </div>
      ) : beats.length === 0 ? (
        <div className="text-center py-20 text-[#737373]">
          <p className="text-lg">No featured beats yet.</p>
          <Link
            to="/catalog"
            className="inline-block mt-4 text-gold text-xs tracking-[0.25em] uppercase hover:underline"
          >
            Browse full catalog →
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
          {beats.map((beat, i) => (
            <FeaturedCard
              key={beat.id}
              beat={beat}
              index={i}
              isActive={currentTrack?.id === beat.id}
              isPlaying={currentTrack?.id === beat.id && isPlaying}
              onPlay={() => playTrack(beat)}
              onLicense={() => setLicenseBeat(beat)}
            />
          ))}
        </div>
      )}

      <div className="mt-14 flex justify-center">
        <Link
          to="/catalog"
          data-testid="featured-view-catalog-cta"
          className="metal-gold-surface inline-flex items-center gap-2 px-10 py-4 font-semibold text-sm tracking-widest uppercase rounded-full transition-all hover:brightness-105"
        >
          View Full Catalog
          <ArrowRight size={16} />
        </Link>
      </div>

      {licenseBeat && (
        <LicenseModal beat={licenseBeat} onClose={() => setLicenseBeat(null)} />
      )}
    </section>
  );
}

function FeaturedCard({ beat, index, isActive, isPlaying, onPlay, onLicense }) {
  return (
    <div
      data-testid={`featured-card-${beat.id}`}
      className={`group chrome-panel rounded-xl overflow-hidden transition-colors animate-fade-slide-up ${
        isActive ? "border-gold/40" : "hover:border-gold/30"
      }`}
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      <div
        className="relative aspect-square bg-[#080808] cursor-pointer overflow-hidden"
        onClick={onPlay}
      >
        {beat.cover_art_url ? (
          <img
            src={beat.cover_art_url}
            alt={beat.title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#1A1A1A] text-6xl font-serif">
            S
          </div>
        )}
        <div
          className={`absolute inset-0 flex items-center justify-center bg-black/40 transition-opacity ${
            isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          }`}
        >
          <div
            className={`w-14 h-14 flex items-center justify-center rounded-full transition-all ${
              isActive ? "metal-gold-surface" : "bg-white/10 text-white backdrop-blur"
            }`}
          >
            {isPlaying ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
          </div>
        </div>
      </div>

      <div className="p-4">
        <h3
          className={`text-sm font-medium truncate ${
            isActive ? "text-gold" : "text-[#EAEAEA]"
          }`}
          title={beat.title}
        >
          {beat.title}
        </h3>
        <div className="flex items-center gap-2 mt-1 text-[10px] tracking-wider uppercase text-[#737373]">
          <span>{beat.bpm} BPM</span>
          <span>·</span>
          <span>{beat.key}</span>
          {beat.genre?.[0] && (
            <>
              <span>·</span>
              <span className="truncate">{beat.genre[0]}</span>
            </>
          )}
        </div>
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-gold font-medium">
            ${beat.pricing?.standard ?? "—"}
          </span>
          <button
            data-testid={`featured-license-${beat.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onLicense();
            }}
            className="w-9 h-9 flex items-center justify-center chrome-dark text-[#A1A1A1] hover:text-[#F2D072] rounded-full transition-all"
          >
            <Plus size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
